import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputLabel,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import SubHeader from "../../../../../components/SubHeader";
import LoanList from "./LoanList";
import styles from "./style";
import Modal from "../../../../../components/Modal";
import { formatDate } from "../../../../../utils/DateFormat";
import { Download } from "@mui/icons-material";

// --------------------------------------------------------------------------
// Main loan page
// --------------------------------------------------------------------------

const LoanPage = ({
  data,
  open,
  getLoanList,
  getStartPeriod,
  tax,
  period,
  setOpen,
  handleAdd,
  handleUpdate,
  total,
  page,
  setPage,
  handleDelete,
  handleFilter,
  handleRequest,
  loading,
  employeeData,
  handleEmployee,
  permission,
  role,
  handleDownload
}) => {
  const theme = useTheme();
  const isEmployeeRole = role === "Employee" || role === "employee";
  const [item, setItem] = useState();
  const [change, setChange] = useState(false);
  const [from, setFrom] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState("");
  const [repdate, setRepdate] = useState("");
  const [loan, setLoan] = useState("");
  const [repaymentAmount, setRepaymentAmount] = useState("");
  const [numberRepayments, setNumberRepayments] = useState("");
  const [des, setDes] = useState("");
  const [remark, setRemark] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Manual");
  const [error, setError] = useState(""); // New state for error message

  useEffect(() => {
    if (from === "edit" && item) {
      setOptionValue(item?.employeeDetails?.personalInfo.email || "");
      setEmployeeId(item?.employeeDetails?._id || "");
      setDate(item?.taxYear?.toString() || "");
      setRepdate(item?.repayStart || "");
      setLoan(item?.loanAmt?.toString() || "");
      setRepaymentAmount(item?.repayAmt?.toString() || "");
      setNumberRepayments(item?.numRepay?.toString() || "");
      setDes(item?.desc || "");
      setRemark(item?.remark || "");
      setPaymentMethod(item?.paymentMethod || "Manual");
      setChange(false);
      setError(""); // Reset error on edit
    } else if (from !== "edit") {
      setPaymentMethod("Manual"); // Reset to default when creating new
    }
  }, [item, from]);

  useEffect(() => {
    if (from === "edit" && item?.taxYear) {
      const selectedTaxYear = tax?.find(
        (t) => new Date(t.start).toISOString() === item.taxYear
      );
      if (selectedTaxYear) {
        getStartPeriod(selectedTaxYear._id);
      }
    }
  }, [from, item?.taxYear, tax]);

  // Validate repayment amount against loan amount
  const validateRepayment = (newRepaymentAmount, newNumberRepayments) => {
    const loanNum = Number(loan);
    const repaymentNum = Number(newRepaymentAmount);
    const monthsNum = Number(newNumberRepayments);
    if (loanNum > 0 && repaymentNum >= 0 && monthsNum >= 0) {
      const totalRepayment = repaymentNum * monthsNum;
      if (totalRepayment > loanNum) {
        setError("Total repayment cannot exceed the loan amount.");
        return false;
      }
    }
    setError("");
    return true;
  };

  // Handle repayment amount change
  const handleRepaymentAmountChange = (value) => {
    const numericValue = value === "" ? "" : Number(value);
    if (numericValue < 0) return; // Prevent negative values
    if (validateRepayment(numericValue, numberRepayments)) {
      setRepaymentAmount(numericValue);
      setChange(true);
    }
  };

  // Handle number of repayments change
  const handleNumberRepaymentsChange = (value) => {
    const numericValue = value === "" ? "" : Number(value);
    if (numericValue < 0) return; // Prevent negative values
    if (validateRepayment(repaymentAmount, numericValue)) {
      setNumberRepayments(numericValue);
      setChange(true);
    }
  };

  const handleLoanChange = (value) => {
    const numericValue = value === "" ? "" : Number(value);
    if (numericValue < 0) return; // Prevent negative values
    setLoan(numericValue);
    setChange(true);
    validateRepayment(repaymentAmount, numberRepayments);
  };

  const handleData = () => {
    if (from === "edit") {
      handleUpdate(
        item?._id,
        employeeId,
        date,
        repdate,
        loan,
        repaymentAmount,
        numberRepayments,
        des,
        remark,
        paymentMethod
      );
    } else {
      handleAdd(
        employeeId,
        date,
        repdate,
        loan,
        repaymentAmount,
        numberRepayments,
        des,
        remark,
        paymentMethod
      );
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setOpen(false);
    setItem(null);
    setFrom("");
    setOptionValue("");
    setEmployeeId("");
    setDate("");
    setRepdate("");
    setLoan("");
    setRepaymentAmount("");
    setNumberRepayments("");
    setDes("");
    setRemark("");
    setPaymentMethod("Manual");
    setChange(false);
    setError(""); // Reset error
  };

  // Calculate Total Repayment for display only
  const totalRepayment = Number(repaymentAmount) * Number(numberRepayments) || "";

  // Check if any field has changed in edit mode
  const hasChanges = () => {
    const originalRepdate = item?.repayStart
      ? dayjs(item.repayStart).format("MMMM YYYY")
      : "";
    const changes = {
      date: date !== (item?.taxYear || ""),
      repdate: repdate !== originalRepdate,
      loan: loan !== (item?.loanAmt?.toString() || ""),
      repaymentAmount: repaymentAmount !== (item?.repayAmt?.toString() || ""),
      numberRepayments: numberRepayments !== (item?.numRepay?.toString() || ""),
      des: des !== (item?.desc || ""),
      remark: remark !== (item?.remark || ""),
      paymentMethod: paymentMethod !== (item?.paymentMethod || "Manual")
    };
    return Object.values(changes).some(change => change);
  };

  const getMonthYearLabel = (date) => {
    if (!date) return "";
    return dayjs(date).format("MMMM YYYY"); // e.g. "July 2025"
  };

  useEffect(() => {
    if (item?.repayStart && period?.length) {
      const monthYear = dayjs(item.repayStart).format("MMMM YYYY"); // e.g. "July 2025"
      if (period.includes(monthYear)) {
        setRepdate(monthYear);  // set dropdown value
      } else {
        setRepdate(""); // fallback if not found
      }
    }
  }, [item?.repayStart, period]);

  return (
    <>
      <Box
        px="15px"
        pt="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5" sx={styles.heading}>
          {isEmployeeRole ? "My Loan" : "Loan"}
        </Typography>
        <Box display="flex">

          <Button
            sx={styles.exportBtn}
            onClick={handleDownload}
          >
            <Download />
            Export Pdf
          </Button>
          <Box
            display="flex"
            justifyContent={{ xs: "start", md: "end" }}
            alignItems={{ xs: "start", md: "end" }}
          >
            {permission || role === "Admin" ? (
              <Button
                variant="contained"
                color="success"
                sx={styles.checkoutBtn}
                onClick={() => {
                  setOpen(true);
                  setFrom("create");
                }}
              >
                + Add Loan
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.white.main,
          m: 2,
          borderRadius: "20px",
        }}
      >
        <SubHeader
          title={isEmployeeRole ? "My Loan List" : "Loan List"}
          search={true}
          sortBy={true}
          onCompanyChange={getLoanList}
          onchange={(e) => handleFilter(e)}
          filter={handleFilter}
          isShowFilter={false}
          handleSort={(e) => handleFilter("", e)}
        />

        <LoanList
          page={page}
          total={total}
          setFrom={setFrom}
          setPage={setPage}
          data={data}
          handleDelete={handleDelete}
          setItem={setItem}
          setOpen={setOpen}
          setChange={setChange}
          permission={permission}
          role={role}
        />
      </Box>
      <Modal
        open={open}
        setOpen={handleCloseModal}
        disabled={
          !employeeId ||
          !date ||
          !repdate ||
          !loan ||
          !repaymentAmount ||
          !numberRepayments ||
          (from === "edit" && !hasChanges()) ||
          (Number(repaymentAmount) * Number(numberRepayments) > Number(loan)) ||
          error !== ""
        }
        btnText={from === "edit" ? "Save Changes" : "Add New"}
        handleCheck={() => handleData()}
        title={from === "edit" ? "Edit Loan" : "Create Loan"}
        handleRequest={handleRequest}
      >
        <Box>
          <Box sx={styles.modalGrid}>
            {from === "edit" ? (
              <Box sx={{ width: { xs: "100%", md: "50%" }, my: 1 }}>
                <InputLabel sx={styles.label}>Employee Email</InputLabel>
                <TextField
                  value={item?.employeeDetails?.personalInfo?.email || ""}
                  inputProps={{ readOnly: true }}
                  rows={2}
                  sx={styles.reason}
                  InputProps={{
                    sx: {
                      "&.Mui-disabled": {
                        WebkitTextFillColor: theme.palette.text.primary,
                        color: theme.palette.text.primary,
                      },
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ width: { xs: "100%", md: "50%" }, my: 1 }}>
                <Autocomplete
                  id="autocomplete"
                  options={employeeData || []}
                  getOptionLabel={(option) => option?.personalInfo?.email || ""}
                  onInputChange={(event, newInputValue) => {
                    handleEmployee(newInputValue);
                    setChange(true);
                  }}
                  onChange={(event, newValue) => {
                    setOptionValue(newValue?.personalInfo?.email || "");
                    setEmployeeId(newValue?._id || "");
                    setChange(true);
                  }}
                  renderInput={(params) => (
                    <>
                      <InputLabel sx={styles.label}>Employee Email</InputLabel>
                      <TextField
                        {...params}
                        sx={{ ...styles.modalInput, ...styles.inputSpace }}
                        variant="outlined"
                        placeholder="Employee Email"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    </>
                  )}
                />
              </Box>
            )}
            <Box sx={styles.modalRight}>
              <InputLabel sx={styles.label}>Loan Tax Year</InputLabel>
              <Select
                value={date || ""}
                onChange={(e) => {
                  setDate(e.target.value);
                  getStartPeriod(e.target.value);
                  setChange(true);
                }}
                sx={styles.dropdown}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: "200px",
                      mt: 1,
                      maxHeight: 200,
                      padding: "8px",
                    },
                  },
                }}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Type
                </MenuItem>
                {tax?.map((items, index) => (
                  <MenuItem
                    sx={{
                      ...styles.dropdown,
                      fontSize: "12px",
                      padding: "8px 16px",
                    }}
                    key={index}
                    value={new Date(items?.start).toISOString()}
                    onClick={() => getStartPeriod(items?._id)}
                  >
                    {formatDate(items?.start)} - {formatDate(items?.end)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <Box sx={styles.modalGrid}>
            <Box sx={{ width: { xs: "100%", md: "50%" }, my: 1 }}>
              <InputLabel sx={styles.label}>Repayment Start Period</InputLabel>
              <Select
                value={repdate || ""}
                onChange={(e) => {
                  setRepdate(e.target.value);
                  setChange(true);
                }}
                sx={{
                  ...styles.dropdown,
                  height: "35px",
                  fontSize: "14px",
                  padding: "0 10px",
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                      fontSize: "14px",
                    },
                  },
                }}
              >
                <MenuItem disabled value="">
                  Select Type
                </MenuItem>
                {period?.map((items, index) => (
                  <MenuItem sx={styles.dropdown} key={index} value={items}>
                    {items}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={styles.modalRight}>
              <InputLabel sx={styles.label}>Loan Amount</InputLabel>
              <TextField
                value={loan || ""}
                placeholder="Loan Amount"
                onChange={(e) => handleLoanChange(e.target.value)}
                rows={2}
                type="number"
                sx={styles.reason}
                InputProps={{
                  sx: {
                    "&.Mui-disabled": {
                      WebkitTextFillColor: theme.palette.text.primary,
                      color: theme.palette.text.primary,
                    },
                  },
                  onKeyDown: (e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault(); // Prevent invalid characters
                    }
                  },
                }}
              />
            </Box>
          </Box>
          <Box sx={styles.modalGrid}>
            <Box sx={{ width: { xs: "100%", md: "50%" }, my: 1 }}>
              <InputLabel sx={styles.label}>Monthly Amount</InputLabel>
              <TextField
                value={repaymentAmount || ""}
                placeholder="Monthly Amount"
                onChange={(e) => handleRepaymentAmountChange(e.target.value)}
                rows={2}
                type="number"
                sx={styles.reason}
                error={!!error} // Show error state if error exists
                helperText={error} // Display error message
                InputProps={{
                  onKeyDown: (e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault(); // Prevent invalid characters
                    }
                  },
                }}
              />
            </Box>
            <Box sx={styles.modalRight}>
              <InputLabel sx={styles.label}>Number Of Month</InputLabel>
              <TextField
                value={numberRepayments || ""}
                placeholder="Number Of Months"
                onChange={(e) => handleNumberRepaymentsChange(e.target.value)}
                rows={2}
                type="number"
                sx={styles.reason}
                error={!!error} // Show error state if error exists
                helperText={error} // Display error message
                InputProps={{
                  onKeyDown: (e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault(); // Prevent invalid characters
                    }
                  },
                }}
              />
            </Box>
          </Box>
          <Box sx={styles.modalGrid}>
            <Box sx={{ width: { xs: "100%", md: "50%" }, my: 1 }}>
              <InputLabel sx={styles.label}>Total Repayment</InputLabel>
              <TextField
                value={totalRepayment}
                placeholder="Total Repayment"
                disabled
                rows={2}
                type="number"
                sx={styles.reason}
                InputProps={{
                  sx: {
                    "&.Mui-disabled": {
                      WebkitTextFillColor: theme.palette.text.primary,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              />
            </Box>
            <Box sx={styles.modalRight}>
              <InputLabel sx={styles.label}>Description</InputLabel>
              <TextField
                value={des || ""}
                placeholder="Description"
                onChange={(e) => {
                  setDes(e.target.value);
                  setChange(true);
                }}
                rows={2}
                sx={styles.reason}
              />
            </Box>
          </Box>
          <Box sx={{ my: 1 }}>
            <InputLabel sx={styles.label}>Payment Method</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value);
                setChange(true);
              }}
              sx={{
                ...styles.reason,
                "& .MuiOutlinedInput-root": {
                  minHeight: "56.5px",
                  height: "56.5px",
                  "& .MuiSelect-select": {
                    py: 1,
                    display: "flex",
                    alignItems: "center"
                  }
                }
              }}
              displayEmpty
            >
              <MenuItem value="Manual">Manual</MenuItem>
              <MenuItem value="Auto">Auto</MenuItem>
            </Select>
          </Box>
          <InputLabel sx={styles.label}>Remark</InputLabel>
          <TextField
            multiline={true}
            value={remark || ""}
            onChange={(e) => {
              setRemark(e.target.value);
              setChange(true);
            }}
            rows={2}
            sx={styles.reason}
            placeholder="Remark"
          />
        </Box>
      </Modal>
    </>
  );
};

// Export loan page
export default LoanPage;
